import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  // Input,
  // InputGroup,
  Button,
} from "@chakra-ui/react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { logout } from "../store/slices/authSlice";
import {
  FiHome,
  FiUser,
  FiLogOut,
  FiPackage,
  FiBarChart2,
  FiSettings,
  FiUpload,
  // FiPlus,
  // FiSend,
  FiBell,
  FiCheckSquare,
  FiMapPin,
  FiMenu,
  FiX,
} from "react-icons/fi";
import { GoBell } from "react-icons/go";
import { HiOutlineSparkles } from "react-icons/hi2";
import { useState } from "react";
import type { IconType } from "react-icons";

type NavItemProps = {
  icon: IconType;
  label: string;
  path: string;
  active: boolean;
  onClick?: () => void;
};

function NavItem({ icon: Icon, label, path, active, onClick }: NavItemProps) {
  if (onClick) {
    return (
      <Box
        as="button"
        onClick={onClick}
        w="full"
        px={4}
        py={3}
        borderRadius="lg"
        bg={active ? "blue.50" : "transparent"}
        color={active ? "blue.600" : "gray.700"}
        fontWeight={active ? "600" : "500"}
        _hover={{ bg: active ? "blue.50" : "gray.50" }}
        transition="all 0.2s"
        cursor="pointer"
        display="block"
        textAlign="left">
        <HStack gap={3}>
          <Icon size={20} />
          <Text fontSize="sm">{label}</Text>
        </HStack>
      </Box>
    );
  }

  return (
    <Link to={path} style={{ textDecoration: "none", display: "block", width: "100%" }}>
      <Box
        w="full"
        px={4}
        py={3}
        borderRadius="lg"
        bg={active ? "blue.50" : "transparent"}
        color={active ? "blue.600" : "gray.700"}
        fontWeight={active ? "600" : "500"}
        _hover={{ bg: active ? "blue.50" : "gray.50" }}
        transition="all 0.2s"
        textAlign="left">
        <HStack gap={3}>
          <Icon size={20} />
          <Text fontSize="sm">{label}</Text>
        </HStack>
      </Box>
    </Link>
  );
}

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    // Clear any other potential storage items
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <Flex h="100vh" bg="gray.50">
      {/* Sidebar */}
      <Box
        w="260px"
        bg="white"
        borderRight="1px"
        borderColor="gray.200"
        position="fixed"
        h="full"
        overflowY="auto"
        display={{ base: isSidebarOpen ? "block" : "none", md: "block" }}
        zIndex={10}
        left={{ base: isSidebarOpen ? 0 : "-260px", md: 0 }}
        transition="left 0.3s">
        <VStack align="stretch" h="full" gap={0}>
          {/* Logo */}
          <Box p={4} borderBottom="1px" borderColor="gray.200">
            <img src="/assets/logo/logo.svg" />
          </Box>

          {/* User Info
          <Box p={4} borderBottom="1px" borderColor="gray.200">
            <VStack align="stretch" gap={1}>
              <Text
                fontSize="sm"
                fontWeight="600"
                color="gray.800"
                lineClamp={1}>
                {user?.first_name} {user?.last_name}
              </Text>
              <Text fontSize="xs" color="gray.500" lineClamp={1}>
                {user?.email}
              </Text>
            </VStack>
          </Box> */}

          {/* Navigation */}
          <VStack align="stretch" gap={1} p={4} flex={1}>
            <Text fontSize="xs" fontWeight="600" color="gray.500" px={4} mb={2}>
              MAIN MENU
            </Text>
            <NavItem
              icon={FiHome}
              label="Dashboard"
              path="/dashboard"
              active={isActive("/dashboard")}
            />

            <NavItem
              icon={HiOutlineSparkles}
              label="RX Assistant"
              path="/ai-assistant/chat"
              active={isActive("/ai-assistant/chat")}
            />
            <NavItem
              icon={FiBarChart2}
              label="Sales Analytics"
              path="/analytics/sales"
              active={isActive("/analytics/sales")}
            />
            {/* <NavItem icon={GiMedicines} label="inventory" path="/inventory" /> */}
            <NavItem
              icon={FiBell}
              label="Notifications"
              path="/notifications"
              active={isActive("/notifications")}
            />

            {user?.role === "admin" && (
              <>
                <Text
                  fontSize="xs"
                  fontWeight="600"
                  color="gray.500"
                  px={4}
                  mt={4}
                  mb={2}>
                  ADMIN
                </Text>
                <NavItem
                  icon={FiUpload}
                  label="Data Upload"
                  path="/data-upload"
                  active={isActive("/data-upload")}
                />
                <NavItem
                  icon={FiPackage}
                  label="Data Summary"
                  path="/data-summary"
                  active={isActive("/data-summary")}
                />
                <NavItem
                  icon={FiPackage}
                  label="Master Drug Index"
                  path="/master-drug-index"
                  active={isActive("/master-drug-index")}
                />
                <NavItem
                  icon={FiCheckSquare}
                  label="Normalization Review"
                  path="/normalization-review"
                  active={isActive("/normalization-review")}
                />
                <NavItem
                  icon={FiMapPin}
                  label="Locations"
                  path="/locations"
                  active={isActive("/locations")}
                />
                {/* <NavItem
                  icon={MdOutlineLocalPharmacy}
                  label="Pharmacies"
                  path="/pharmacy"
                /> */}
              </>
            )}

            <Text
              fontSize="xs"
              fontWeight="600"
              color="gray.500"
              px={4}
              mt={4}
              mb={2}>
              SETTINGS
            </Text>
            <NavItem
              icon={FiUser}
              label="Profile"
              path="/profile"
              active={isActive("/profile")}
            />

            {user?.role === "admin" && (
              <NavItem
                icon={FiSettings}
                label="Settings"
                path="/settings"
                active={isActive("/settings")}
              />
            )}
          </VStack>

          {/* Logout */}
          <Box p={4} borderTop="1px" borderColor="gray.200">
            <Box
              as="button"
              w="full"
              px={4}
              py={3}
              borderRadius="lg"
              bg="red.50"
              color="red.600"
              fontWeight="500"
              _hover={{ bg: "red.100" }}
              transition="all 0.2s"
              onClick={handleLogout}>
              <HStack gap={3}>
                <FiLogOut size={20} />
                <Text fontSize="sm">Logout</Text>
              </HStack>
            </Box>
          </Box>
        </VStack>
      </Box>

      {/* Overlay for mobile */}
      <Box
        display={{ base: isSidebarOpen ? "block" : "none", md: "none" }}
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.500"
        zIndex={5}
        onClick={() => setIsSidebarOpen(false)}
      />

      {/* Main Content */}
      <Box ml={{ base: 0, md: "260px" }} flex={1} overflowY="auto">
        <header>
          <Box
            p={4}
            borderBottom="1px"
            borderColor="#F0F0F0"
            bgColor={"#fff"}
            className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                display={{ base: "block", md: "none" }}
                variant="ghost"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                p={2}>
                {isSidebarOpen ? <FiX size={20} /> : <FiMenu size={20} />}
              </Button>
              {/* <div className="w-75">
                <InputGroup
                  startElement={<FiPlus />}
                  endElement={
                    <button className="bg-[#0142B1]! h-6 w-6 flex items-center justify-center rounded-md text-white">
                      <FiSend color="#fff" size={12} />
                    </button>
                  }>
                  <Input
                    size={"md"}
                    className="w-full"
                    placeholder="Ask RX Assistant anything.."
                  />
                </InputGroup>
              </div> */}
            </div>

            <div className="flex items-center gap-4 w-fit">
              <Link to="/notifications">
                <button className="bg-[#E6ECF7]! p-2 w-7.5 h-7.5 flex items-center justify-center rounded-md relative">
                  <GoBell />
                </button>
              </Link>

              <Link to="/profile">
                <button className="bg-[#E6ECF7]! p-2 w-7.5 h-7.5 flex items-center justify-center rounded-md">
                  <FiUser />
                </button>
              </Link>
            </div>
          </Box>
        </header>
        <Box className="p-3 md:p-8">
          <Outlet />
        </Box>
      </Box>
    </Flex>
  );
}
